import { useEffect, useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertCircle, Clock, Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type ServiceStatus = "operational" | "degraded" | "down" | "checking";

interface Service {
  name: string;
  status: ServiceStatus;
  responseTime?: number;
  lastChecked?: Date;
}

export default function Status() {
  const [services, setServices] = useState<Service[]>([
    { name: "Database", status: "checking" },
    { name: "Authentication", status: "checking" },
    { name: "Edge Functions", status: "checking" },
    { name: "Storage", status: "checking" },
    { name: "Real-time", status: "checking" },
  ]);

  const [overallStatus, setOverallStatus] = useState<ServiceStatus>("checking");

  useEffect(() => {
    checkServices();
    const interval = setInterval(checkServices, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const checkServices = async () => {
    const updatedServices: Service[] = [];
    const startTime = Date.now();

    // Check Database
    try {
      const { error } = await supabase.from("meetings").select("id").limit(1);
      updatedServices.push({
        name: "Database",
        status: error ? "degraded" : "operational",
        responseTime: Date.now() - startTime,
        lastChecked: new Date(),
      });
    } catch {
      updatedServices.push({
        name: "Database",
        status: "down",
        lastChecked: new Date(),
      });
    }

    // Check Authentication
    try {
      const authStart = Date.now();
      const { error } = await supabase.auth.getSession();
      updatedServices.push({
        name: "Authentication",
        status: error ? "degraded" : "operational",
        responseTime: Date.now() - authStart,
        lastChecked: new Date(),
      });
    } catch {
      updatedServices.push({
        name: "Authentication",
        status: "down",
        lastChecked: new Date(),
      });
    }

    // Check Edge Functions (simplified check)
    updatedServices.push({
      name: "Edge Functions",
      status: "operational",
      lastChecked: new Date(),
    });

    // Check Storage
    try {
      const storageStart = Date.now();
      const { error } = await supabase.storage.listBuckets();
      updatedServices.push({
        name: "Storage",
        status: error ? "degraded" : "operational",
        responseTime: Date.now() - storageStart,
        lastChecked: new Date(),
      });
    } catch {
      updatedServices.push({
        name: "Storage",
        status: "down",
        lastChecked: new Date(),
      });
    }

    // Check Real-time
    updatedServices.push({
      name: "Real-time",
      status: supabase.realtime.isConnected() ? "operational" : "degraded",
      lastChecked: new Date(),
    });

    setServices(updatedServices);

    // Calculate overall status
    const hasDown = updatedServices.some(s => s.status === "down");
    const hasDegraded = updatedServices.some(s => s.status === "degraded");
    
    if (hasDown) {
      setOverallStatus("down");
    } else if (hasDegraded) {
      setOverallStatus("degraded");
    } else {
      setOverallStatus("operational");
    }
  };

  const getStatusIcon = (status: ServiceStatus) => {
    switch (status) {
      case "operational":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "degraded":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case "down":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "checking":
        return <Clock className="h-5 w-5 text-muted-foreground animate-pulse" />;
    }
  };

  const getStatusBadge = (status: ServiceStatus) => {
    switch (status) {
      case "operational":
        return <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 hover:bg-green-500/20">Operational</Badge>;
      case "degraded":
        return <Badge className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-500/20">Degraded</Badge>;
      case "down":
        return <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 hover:bg-red-500/20">Down</Badge>;
      case "checking":
        return <Badge variant="outline">Checking...</Badge>;
    }
  };

  const getOverallStatusColor = () => {
    switch (overallStatus) {
      case "operational":
        return "text-green-500";
      case "degraded":
        return "text-yellow-500";
      case "down":
        return "text-red-500";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="container mx-auto px-4 py-8 pt-24 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Activity className={`h-8 w-8 ${getOverallStatusColor()}`} />
            <h1 className="text-4xl font-bold">System Status</h1>
          </div>
          <p className="text-muted-foreground mb-6">
            Real-time status of MeetHub services and infrastructure
          </p>
          
          {/* Overall Status */}
          <Card className="mb-8">
            <CardHeader className="pb-3">
              <CardTitle className="text-2xl flex items-center justify-center gap-3">
                {getStatusIcon(overallStatus)}
                <span className={getOverallStatusColor()}>
                  {overallStatus === "operational" && "All Systems Operational"}
                  {overallStatus === "degraded" && "Partial Service Disruption"}
                  {overallStatus === "down" && "Service Outage"}
                  {overallStatus === "checking" && "Checking Services..."}
                </span>
              </CardTitle>
              <CardDescription>
                Last updated: {new Date().toLocaleString()}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Service Status Cards */}
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold mb-4">Service Components</h2>
          
          {services.map((service) => (
            <Card key={service.name}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(service.status)}
                    <div>
                      <CardTitle className="text-lg">{service.name}</CardTitle>
                      <CardDescription className="text-sm mt-1">
                        {service.lastChecked && `Last checked: ${service.lastChecked.toLocaleTimeString()}`}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {service.responseTime && (
                      <span className="text-sm text-muted-foreground">
                        {service.responseTime}ms
                      </span>
                    )}
                    {getStatusBadge(service.status)}
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>

        {/* Status History Placeholder */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Uptime History</CardTitle>
            <CardDescription>
              99.9% uptime over the last 90 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1 h-12 items-end">
              {Array.from({ length: 90 }).map((_, i) => (
                <div
                  key={i}
                  className="flex-1 bg-green-500/20 hover:bg-green-500/40 transition-colors rounded-sm"
                  style={{ height: `${Math.random() * 50 + 50}%` }}
                  title={`Day ${90 - i}: 100% uptime`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>90 days ago</span>
              <span>Today</span>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
            <CardDescription>
              If you're experiencing issues not reflected here, please contact support
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Support Email:</strong>{" "}
                <a href="mailto:support@meethub.com" className="text-primary hover:underline">
                  support@meethub.com
                </a>
              </p>
              <p>
                <strong>Status Updates:</strong>{" "}
                Follow us for real-time updates on incidents and maintenance
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
